import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/site/Layout";
import { OfficeMap } from "@/components/site/OfficeMap";
import { Phone, Mail, Globe } from "lucide-react";
import { getPublicSettings, listAllProductsPublic } from "@/lib/content.functions";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { ConsultationBookingForm } from "@/components/forms/ConsultationBookingForm";
import { DefaultContactForm } from "@/components/forms/DefaultContactForm";
import { useT } from "@/lib/i18n";
import siteConfig from "@/config/site.json";

const settingsQueryOptions = queryOptions({
  queryKey: ["public-settings"],
  queryFn: () => getPublicSettings(),
});

const productsQueryOptions = queryOptions({
  queryKey: ["all-products"],
  queryFn: () => listAllProductsPublic(),
});

export const Route = createFileRoute("/contact")({
  component: Booking,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(settingsQueryOptions),
      context.queryClient.ensureQueryData(productsQueryOptions),
    ]),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" role="alert">
        <h1 className="font-serif text-3xl mb-4">Couldn't load the form</h1>
        <p className="text-muted-foreground">{error.message}</p>
      </div>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl">Page not found</h1>
      </div>
    </Layout>
  ),
  head: () => ({
    meta: [
      { title: "Contact — PULSO33" },
      {
        name: "description",
        content:
          "Book a 45-min discovery call with PULSO33. Tell us about your project — we'll respond with a concrete plan.",
      },
      { property: "og:title", content: "Contact PULSO33" },
      {
        property: "og:description",
        content:
          "Book a 45-min discovery call. We'll scope your project and respond with a concrete plan.",
      },
      { property: "og:url", content: "https://pulso33.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/contact" }],
  }),
});

function Booking() {
  const { tk } = useT();
  const { data: settings } = useSuspenseQuery(settingsQueryOptions);
  const { data: productsData } = useSuspenseQuery(productsQueryOptions);

  const consultationProducts = (productsData.products ?? [])
    .filter((p) => p.form_key === "booking-consultation" && p.active === true)
    .sort((a, b) => a.sort_order - b.sort_order);
  const consultationProduct = consultationProducts[0] ?? null;
  const paymentEnabled =
    (settings.settings.consultation_payment_enabled ?? true) && Boolean(consultationProduct);

  const phone = siteConfig.contact.phones[0]?.number ?? "";
  const email = siteConfig.contact.emails[0]?.address ?? "info@pulso33.com";

  return (
    <Layout>
      {paymentEnabled && <PaymentTestModeBanner />}
      {paymentEnabled ? null : (
        <section className="border-b border-gold/15">
          <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 text-center">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("contact.kicker", "Contact")}
            </span>
            <h1 className="font-serif text-5xl md:text-6xl mt-5">
              {tk("contact.title.a", "Let's talk about ")}
              <em className="not-italic gold-gradient">{tk("contact.title.b", "your project")}</em>
            </h1>
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("contact.intro", "Send us a short brief — what you want to build, your timeline, anything we should know. We'll respond with a concrete plan.")}
            </p>
          </div>
        </section>
      )}

      {paymentEnabled && consultationProduct ? (
        <ConsultationBookingForm product={consultationProduct} products={consultationProducts} />
      ) : (
        <DefaultContactForm />
      )}

      <section className="border-t border-gold/15 bg-card/20">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { i: Phone, l: tk("contact.phone", "Phone / WhatsApp"), v: [phone] },
            { i: Mail, l: "Email", v: [email] },
            { i: Globe, l: tk("contact.where", "Where we work"), v: [tk("contact.remote", "Remote-first · Europe & Americas")] },
          ].map(({ i: Icon, l, v }) => (
            <div key={l} className="flex gap-4">
              <Icon size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80">{l}</div>
                {v.map((line) => (
                  <div key={line} className="text-foreground/85 mt-1 text-sm">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <OfficeMap />
    </Layout>
  );
}
